import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, TIERS, TIER_MAP } from "@/lib/api";
import { T } from "@/lib/testIds";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIMES = ["9:00 AM – 12:00 PM", "12:00 PM – 3:00 PM", "3:00 PM – 6:00 PM", "6:00 PM – 9:00 PM"];
const PALETTES = [
  { id: "whites-greens", label: "Whites & Greens" },
  { id: "soft-pastels", label: "Soft Pastels" },
  { id: "bright-vibrant", label: "Bright & Vibrant" },
  { id: "florists-choice", label: "Florist's Choice" },
];
const PAYMENTS = [
  { id: "upi", label: "UPI" },
  { id: "credit-card", label: "Credit Card" },
  { id: "bank-transfer", label: "Bank Transfer" },
];

export default function SubscribeDialog({ open, onOpenChange, initialTier = "bloom", initialFrequency = "weekly" }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [createdSub, setCreatedSub] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [studioCfg, setStudioCfg] = useState({ upi_id: "buds@upi", payee_name: "BUDS Floral Club" });
  const [upiTxn, setUpiTxn] = useState("");

  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    address: "", apartment: "",
    tier: initialTier, frequency: initialFrequency,
    preferred_day: "Saturday", preferred_time: TIMES[1],
    special_instructions: "",
    color_palette: "florists-choice",
    payment_method: "upi",
    is_gift: false, recipient_name: "", gift_note: "",
  });

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, tier: initialTier, frequency: initialFrequency }));
      api.get("/studio-config").then((r) => setStudioCfg(r.data)).catch(() => {});
    }
  }, [open, initialTier, initialFrequency]);

  const tier = TIER_MAP[form.tier];
  const monthly = form.frequency === "weekly" ? tier.price * 4 : tier.price * 2;

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(1); setConfirmed(false); setCreatedSub(null); setUpiTxn("");
      setForm((f) => ({
        ...f, full_name: "", phone: "", email: "", address: "", apartment: "",
        special_instructions: "", is_gift: false, recipient_name: "", gift_note: "",
      }));
    }, 500);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep1 = () => {
    if (!form.full_name.trim() || !form.phone.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please fill in your name, phone and a valid email."); return false;
    }
    if (form.is_gift && !form.recipient_name.trim()) {
      toast.error("Please add a name for the gift recipient."); return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (!form.address.trim()) { toast.error("Please add a delivery address."); return false; }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscriptions", form);
      setCreatedSub(data);
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const markPaid = async () => {
    if (!createdSub) return;
    setSubmitting(true);
    try {
      await api.post(`/subscriptions/${createdSub.id}/customer-confirmed`, { upi_txn_id: upiTxn });
      setConfirmed(true);
      toast.success("Thank you — payment noted. Our team will confirm shortly.");
    } catch (e) {
      toast.error("Could not mark as paid. Please try again.");
    } finally { setSubmitting(false); }
  };

  const upiUri = createdSub
    ? `upi://pay?pa=${encodeURIComponent(studioCfg.upi_id)}&pn=${encodeURIComponent(studioCfg.payee_name)}&am=${createdSub.first_payment_amount}&cu=INR&tn=${encodeURIComponent(createdSub.payment_reference)}`
    : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied."));
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] flex items-stretch justify-end bg-[rgba(15,20,17,0.5)] backdrop-blur-[2px]"
          data-testid={T.form.dialog} onClick={close}
        >
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[720px] h-full overflow-y-auto bg-[var(--bg)] px-8 sm:px-14 py-10"
          >
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center gap-6">
                <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">
                  {confirmed ? "Confirmed" : step === 4 ? "Payment" : `Step ${step} of 3`}
                </span>
                {!confirmed && step <= 3 && (
                  <div className="flex gap-1">
                    {[1, 2, 3].map((n) => (
                      <span key={n} className="h-[1px] w-8 transition-colors duration-500"
                        style={{ background: n <= step ? "var(--ink)" : "var(--line-strong)" }} />
                    ))}
                  </div>
                )}
              </div>
              <button data-testid={T.form.close} onClick={close} aria-label="Close" className="hover:rotate-90 transition-transform duration-500">
                <X size={20} />
              </button>
            </div>

            {step <= 3 && (
              <div className="mb-10">
                <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Membership</p>
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 className="font-serif text-4xl sm:text-5xl leading-none">
                    {tier.name}
                    {form.is_gift && <span className="ml-3 inline-flex items-center gap-1.5 align-middle text-[10px] tracking-[0.22em] uppercase border border-[var(--ink)] px-2 py-1 font-sans-editorial"><Gift size={12}/> Gift · 4 deliveries</span>}
                  </h2>
                  <div className="text-right">
                    <p className="font-serif text-2xl">₹{tier.price.toLocaleString("en-IN")}<span className="text-xs tracking-widest uppercase text-[var(--ink-soft)]"> / week</span></p>
                    <p className="text-xs text-[var(--ink-soft)] mt-1">Approx ₹{monthly.toLocaleString("en-IN")} / month</p>
                  </div>
                </div>
                <div className="hairline mt-6" />
              </div>
            )}

            {step === 1 && (
              <div className="space-y-8">
                <Field label="Full name">
                  <input data-testid={T.form.fullName} className="editorial-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="off" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Phone">
                    <input data-testid={T.form.phone} className="editorial-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="off" />
                  </Field>
                  <Field label="Email">
                    <input data-testid={T.form.email} type="email" className="editorial-input" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" />
                  </Field>
                </div>

                {/* Gift toggle */}
                <div className="border-t border-b border-[var(--line-strong)] py-5">
                  <button
                    data-testid={T.form.giftToggle}
                    onClick={() => set("is_gift", !form.is_gift)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <span className="flex items-center gap-3">
                      <Gift size={16} className="text-[var(--sage-deep)]" />
                      <span>
                        <span className="block text-[11px] tracking-[0.24em] uppercase text-[var(--sage-deep)]">Gift a membership</span>
                        <span className="block text-xs text-[var(--ink-soft)] mt-1">Four hand-delivered arrangements with a personal note.</span>
                      </span>
                    </span>
                    <span className="relative w-10 h-5 rounded-full transition-colors duration-500"
                      style={{ background: form.is_gift ? "var(--ink)" : "var(--line-strong)" }}>
                      <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[var(--bg)] transition-transform duration-500"
                        style={{ transform: form.is_gift ? "translateX(20px)" : "translateX(0)" }} />
                    </span>
                  </button>
                  <AnimatePresence>
                    {form.is_gift && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4, ease: [0.22,1,0.36,1] }} className="overflow-hidden">
                        <div className="pt-6 space-y-6">
                          <Field label="Recipient name">
                            <input data-testid={T.form.recipientName} className="editorial-input" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} autoComplete="off" />
                          </Field>
                          <Field label="Personal note (printed on the first bouquet)">
                            <textarea data-testid={T.form.giftNote} className="editorial-input" rows={2} value={form.gift_note} onChange={(e) => set("gift_note", e.target.value)} autoComplete="off" />
                          </Field>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-4">Membership</p>
                  <div className="grid grid-cols-3 gap-2">
                    {TIERS.map((t) => (
                      <button key={t.id} data-testid={T.form.tier(t.id)} onClick={() => set("tier", t.id)}
                        className="text-left border p-3 transition-colors duration-500"
                        style={{
                          borderColor: form.tier === t.id ? "var(--ink)" : "var(--line-strong)",
                          background: form.tier === t.id ? "var(--ink)" : "transparent",
                          color: form.tier === t.id ? "var(--bg)" : "var(--ink)",
                        }}>
                        <p className="font-serif text-lg leading-none">{t.name}</p>
                        <p className="text-[10px] tracking-widest uppercase mt-2 opacity-70">₹{t.price}/wk</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-4">Frequency</p>
                  <div className="grid grid-cols-2 gap-2">
                    {["weekly", "fortnightly"].map((f) => (
                      <button key={f} data-testid={T.form.freq(f)} onClick={() => set("frequency", f)}
                        className="border py-3 uppercase text-[11px] tracking-[0.24em] transition-colors duration-500"
                        style={{
                          borderColor: form.frequency === f ? "var(--ink)" : "var(--line-strong)",
                          background: form.frequency === f ? "var(--ink)" : "transparent",
                          color: form.frequency === f ? "var(--bg)" : "var(--ink)",
                        }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-6 flex justify-end">
                  <button data-testid={T.form.step1Next} onClick={() => validateStep1() && setStep(2)} className="btn-primary">
                    Continue <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-8">
                <Field label="Delivery address (please include 6-digit pincode)">
                  <textarea data-testid={T.form.address} className="editorial-input" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} autoComplete="off" />
                </Field>
                <Field label="Apartment / Villa">
                  <input data-testid={T.form.apartment} className="editorial-input" value={form.apartment} onChange={(e) => set("apartment", e.target.value)} autoComplete="off" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Preferred day">
                    <select data-testid={T.form.day} className="editorial-input" value={form.preferred_day} onChange={(e) => set("preferred_day", e.target.value)}>
                      {DAYS.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred time">
                    <select data-testid={T.form.time} className="editorial-input" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)}>
                      {TIMES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Special instructions">
                  <textarea data-testid={T.form.instructions} className="editorial-input" rows={2} value={form.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} autoComplete="off" />
                </Field>
                <div className="pt-6 flex items-center justify-between">
                  <button data-testid={T.form.step2Back} onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft size={14} /> Back</button>
                  <button data-testid={T.form.step2Next} onClick={() => validateStep2() && setStep(3)} className="btn-primary">Continue <ArrowRight size={14} /></button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-10">
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-4">Preferred colour palette</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {PALETTES.map((p) => (
                      <button key={p.id} data-testid={T.form.palette(p.id)} onClick={() => set("color_palette", p.id)}
                        className="text-left border px-4 py-3 transition-colors duration-500"
                        style={{
                          borderColor: form.color_palette === p.id ? "var(--ink)" : "var(--line-strong)",
                          background: form.color_palette === p.id ? "var(--ink)" : "transparent",
                          color: form.color_palette === p.id ? "var(--bg)" : "var(--ink)",
                        }}>
                        <span className="text-[13px] tracking-wide">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-4">Payment method</p>
                  <div className="grid grid-cols-3 gap-2">
                    {PAYMENTS.map((p) => (
                      <button key={p.id} data-testid={T.form.payment(p.id)} onClick={() => set("payment_method", p.id)}
                        className="border py-3 text-[11px] tracking-[0.2em] uppercase transition-colors duration-500"
                        style={{
                          borderColor: form.payment_method === p.id ? "var(--ink)" : "var(--line-strong)",
                          background: form.payment_method === p.id ? "var(--ink)" : "transparent",
                          color: form.payment_method === p.id ? "var(--bg)" : "var(--ink)",
                        }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t border-[var(--line)] pt-6 text-xs text-[var(--ink-soft)] leading-relaxed">
                  On the next step, prepay the first month via UPI to lock in your first delivery slot.
                </div>
                <div className="pt-2 flex items-center justify-between">
                  <button data-testid={T.form.step3Back} onClick={() => setStep(2)} className="btn-ghost"><ArrowLeft size={14} /> Back</button>
                  <button data-testid={T.form.submit} disabled={submitting} onClick={submit} className="btn-primary">
                    {submitting ? "Submitting…" : "Proceed to payment"} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: PAYMENT */}
            {step === 4 && createdSub && !confirmed && (
              <motion.div
                data-testid={T.form.paymentPanel}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-8"
              >
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Prepay first month</p>
                  <h2 className="font-serif text-4xl sm:text-5xl leading-none">
                    ₹{createdSub.first_payment_amount.toLocaleString("en-IN")}
                  </h2>
                  <p className="text-sm text-[var(--ink-soft)] mt-3">
                    {createdSub.frequency === "weekly" ? "4 weekly" : "2 fortnightly"} deliveries of {TIER_MAP[createdSub.tier].name}.
                  </p>
                </div>

                <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start border border-[var(--line-strong)] p-5">
                  <img
                    alt="UPI QR"
                    src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(upiUri)}&size=200x200&bgcolor=F5F1EA&color=1F2A24&margin=8`}
                    className="w-[180px] h-[180px]"
                  />
                  <div className="space-y-3 text-sm">
                    <Row2 k="UPI ID">
                      <span>{studioCfg.upi_id}</span>
                      <button onClick={() => copy(studioCfg.upi_id)} className="ml-2 text-[var(--ink-soft)] hover:text-[var(--ink)]"><Copy size={12} /></button>
                    </Row2>
                    <Row2 k="Payee">{studioCfg.payee_name}</Row2>
                    <Row2 k="Amount">₹{createdSub.first_payment_amount.toLocaleString("en-IN")}</Row2>
                    <Row2 k="Reference">
                      <span>{createdSub.payment_reference}</span>
                      <button onClick={() => copy(createdSub.payment_reference)} className="ml-2 text-[var(--ink-soft)] hover:text-[var(--ink)]"><Copy size={12} /></button>
                    </Row2>
                    <a data-testid={T.form.upiPay} href={upiUri} className="btn-primary mt-3">Open UPI app <ArrowRight size={14} /></a>
                  </div>
                </div>

                <Field label="UPI transaction reference (optional)">
                  <input data-testid={T.form.upiTxn} className="editorial-input" value={upiTxn} onChange={(e) => setUpiTxn(e.target.value)} autoComplete="off" />
                </Field>

                <div className="border-t border-[var(--line)] pt-5 text-xs text-[var(--ink-soft)] leading-relaxed">
                  After payment, tap the button below. Our team will verify within a few hours and confirm your first delivery by phone or email.
                </div>

                <div className="flex items-center justify-end">
                  <button data-testid={T.form.markPaid} disabled={submitting} onClick={markPaid} className="btn-primary">
                    {submitting ? "Saving…" : "I have paid"} <Check size={14} />
                  </button>
                </div>
              </motion.div>
            )}

            {confirmed && (
              <motion.div
                data-testid={T.form.confirmation}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-2"
              >
                <div className="w-16 h-16 rounded-full border border-[var(--ink)] flex items-center justify-center mb-8">
                  <Check size={22} />
                </div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-4">Welcome</p>
                <h2 className="font-serif text-5xl leading-[1.05] mb-6">You are now part of the BUDS Floral Club.</h2>
                <p className="text-[var(--ink-soft)] leading-relaxed max-w-md mb-10">
                  A welcome note is on its way to your inbox. Our team will verify your prepayment and confirm your first delivery within a few hours.
                </p>
                <div className="border-t border-[var(--line)] pt-6 space-y-3 text-sm">
                  <Row k="Membership" v={createdSub ? TIER_MAP[createdSub.tier].name : tier.name} />
                  <Row k="Frequency" v={(createdSub?.frequency || form.frequency) === "weekly" ? "Weekly" : "Fortnightly"} />
                  <Row k="First payment" v={`₹${(createdSub?.first_payment_amount || monthly).toLocaleString("en-IN")}`} />
                  <Row k="First delivery" v={`${createdSub?.next_delivery || "TBC"} · ${createdSub?.preferred_time || form.preferred_time}`} />
                  {createdSub?.is_gift && <Row k="Gift for" v={createdSub.recipient_name} />}
                </div>
                <button onClick={close} className="btn-primary mt-10">Close</button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">{label}</span>
      {children}
    </label>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[var(--ink-soft)] uppercase tracking-widest text-[10px]">{k}</span>
      <span>{v}</span>
    </div>
  );
}
function Row2({ k, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)]">{k}</span>
      <span className="text-right text-[var(--ink)] flex items-center">{children}</span>
    </div>
  );
}
